import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  subscriptionService,
  BillingProfileData,
  CheckoutPreviewPayload,
  CheckoutSubscriptionPayload,
  SupportTicketPayload,
} from 'src/services/subscriptionService';

// Query Keys
export const subscriptionKeys = {
  all: ['subscription'] as const,
  entitlements: () => [...subscriptionKeys.all, 'entitlements'] as const,
  plans: (country?: string) => [...subscriptionKeys.all, 'plans', country] as const,
  eligibility: () => [...subscriptionKeys.all, 'eligibility'] as const,
  state: () => [...subscriptionKeys.all, 'state'] as const,
  billingProfile: () => [...subscriptionKeys.all, 'billing-profile'] as const,
  paymentMethods: () => [...subscriptionKeys.all, 'payment-methods'] as const,
  billingHistory: (params?: Record<string, any>) =>
    params !== undefined
      ? ([...subscriptionKeys.all, 'billing-history', params] as const)
      : ([...subscriptionKeys.all, 'billing-history'] as const),
  supportConfig: () => [...subscriptionKeys.all, 'support-config'] as const,
  stripeConfig: () => [...subscriptionKeys.all, 'stripe-config'] as const,
};

// ==========================================
// QUERIES
// ==========================================

export function useEntitlementsQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.entitlements(),
    queryFn: () => subscriptionService.getEntitlements(),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled,
  });
}

export function useSubscriptionPlansQuery(country?: string, enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.plans(country),
    queryFn: () => subscriptionService.getSubscriptionPlans(country),
    staleTime: 1000 * 60 * 5,
    // Plan buttons depend on the current subscription, which can change in
    // another screen or browser tab. Always refresh when this screen mounts.
    refetchOnMount: 'always',
    enabled,
  });
}

export function useTrialEligibilityQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.eligibility(),
    queryFn: () => subscriptionService.getTrialEligibility(),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
}

export function useSubscriptionStateQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.state(),
    queryFn: () => subscriptionService.getSubscriptionState(),
    staleTime: 1000 * 30, // 30 seconds
    enabled,
  });
}

export function useBillingProfileQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.billingProfile(),
    queryFn: () => subscriptionService.getBillingProfile(),
    staleTime: 1000 * 60 * 5,
    enabled,
  });
}

export function usePaymentMethodsQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.paymentMethods(),
    queryFn: () => subscriptionService.getPaymentMethods(),
    staleTime: 1000 * 60 * 2,
    enabled,
  });
}

export function useBillingHistoryQuery(
  params: { page?: number; limit?: number; status?: string; search?: string; sort?: string } = {},
  enabled = true,
) {
  return useQuery({
    queryKey: subscriptionKeys.billingHistory(params),
    queryFn: () => subscriptionService.getBillingHistory(params),
    staleTime: 1000 * 60,
    enabled,
  });
}

export function useSupportConfigQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.supportConfig(),
    queryFn: () => subscriptionService.getSupportConfig(),
    staleTime: 1000 * 60 * 10,
    enabled,
  });
}

export function useStripeConfigQuery(enabled = true) {
  return useQuery({
    queryKey: subscriptionKeys.stripeConfig(),
    queryFn: () => subscriptionService.getStripeConfig(),
    staleTime: 1000 * 60 * 60,
    enabled,
  });
}

export function useTermsOfServiceQuery(enabled = true) {
  return useQuery({
    queryKey: ['subscription', 'legal', 'terms-of-service'],
    queryFn: () => subscriptionService.getTermsOfService(),
    staleTime: 1000 * 60 * 60,
    enabled,
  });
}

export function usePrivacyPolicyQuery(enabled = true) {
  return useQuery({
    queryKey: ['subscription', 'legal', 'privacy-policy'],
    queryFn: () => subscriptionService.getPrivacyPolicy(),
    staleTime: 1000 * 60 * 60,
    enabled,
  });
}

// ==========================================
// MUTATIONS
// ==========================================

export function useCheckoutPreviewQuery(params: {
  tier_slug?: string;
  interval: 'month' | 'year';
  price_id?: string | null;
  promo_code?: string;
  country?: string;
  billing_profile_version?: string;
  trial_selection?: boolean;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: [
      'subscription',
      'checkout',
      'preview',
      params.tier_slug,
      params.interval,
      params.price_id,
      params.promo_code,
      params.country,
      params.billing_profile_version,
      params.trial_selection,
    ],
    queryFn: () =>
      subscriptionService.previewCheckout({
        tier_slug: params.tier_slug,
        interval: params.interval,
        price_id: params.price_id,
        promo_code: params.promo_code,
        country: params.country,
        trial_selection: params.trial_selection,
      }),
    enabled: params.enabled ?? true,
    staleTime: 1000 * 60 * 2,
  });
}

export function usePreviewCheckoutMutation() {
  return useMutation({
    mutationFn: (payload: CheckoutPreviewPayload) => subscriptionService.previewCheckout(payload),
  });
}

export function useValidatePromoCodeMutation() {
  return useMutation({
    mutationFn: (payload: {
      code: string;
      tier_slug?: string;
      interval?: 'month' | 'year';
      price_id?: string | null;
      country?: string;
      billing_profile?: Partial<BillingProfileData>;
    }) => subscriptionService.validatePromoCode(payload),
  });
}

export function useCheckoutSubscriptionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CheckoutSubscriptionPayload) =>
      subscriptionService.checkoutSubscription(payload),
    onSuccess: (_data) => {
      // Invalidate subscription queries
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.entitlements() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.billingHistory() });
    },
  });
}

export function useSyncSubscriptionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionService.syncSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.entitlements() });
    },
  });
}

export function useSaveBillingProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: Partial<BillingProfileData>) =>
      subscriptionService.saveBillingProfile(profile),
    onSuccess: (savedData) => {
      // Synchronously hydrate query data to prevent stale race condition
      queryClient.setQueryData(subscriptionKeys.billingProfile(), savedData);
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() });
      queryClient.invalidateQueries({ queryKey: ['subscription', 'checkout', 'preview'] });
    },
  });
}

export function useValidateBillingProfileMutation() {
  return useMutation({
    mutationFn: (profile: Partial<BillingProfileData>) =>
      subscriptionService.validateBillingProfile(profile),
  });
}

export function useCreateSetupIntentMutation() {
  return useMutation({
    mutationFn: () => subscriptionService.createSetupIntent(),
  });
}

export function useSavePaymentMethodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      payment_method_id?: string;
      cardholder_name?: string;
      is_default?: boolean;
    }) => subscriptionService.savePaymentMethod(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.paymentMethods() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
    },
  });
}

export function useSetDefaultPaymentMethodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pmId: string) => subscriptionService.setDefaultPaymentMethod(pmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.paymentMethods() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
    },
  });
}

export function useReplacePaymentMethodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { payment_method_id?: string; cardholder_name?: string }) =>
      subscriptionService.replacePaymentMethod(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.paymentMethods() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.billingHistory() });
    },
  });
}

export function useDeletePaymentMethodMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pmId: string) => subscriptionService.deletePaymentMethod(pmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.paymentMethods() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
    },
  });
}

export function useCancelSubscriptionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ reason, feedback }: { reason: string; feedback?: string }) =>
      subscriptionService.cancelSubscription(reason, feedback),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.entitlements() });
    },
  });
}

export function useReactivateSubscriptionMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionService.reactivateSubscription(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.entitlements() });
    },
  });
}

export function useIntervalSwitchMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetInterval: 'month' | 'year') =>
      subscriptionService.switchInterval(targetInterval),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.plans() });
    },
  });
}

export function useCancelScheduledIntervalSwitchMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionService.cancelScheduledIntervalSwitch(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
    },
  });
}

export function useRetryPastDuePaymentMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionService.retryPastDuePayment(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.state() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.entitlements() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.billingHistory() });
    },
  });
}

export function useContactSupportMutation() {
  return useMutation({
    mutationFn: (payload: SupportTicketPayload) => subscriptionService.contactSupport(payload),
  });
}

export function useDeleteSupportAttachmentMutation() {
  return useMutation({
    mutationFn: (storagePath: string) => subscriptionService.deleteSupportAttachment(storagePath),
  });
}

export function useRecordLegalAcknowledgementMutation() {
  return useMutation({
    mutationFn: (payload?: { termsVersion?: string; privacyVersion?: string } | void) =>
      subscriptionService.recordLegalAcknowledgement(
        payload?.termsVersion,
        payload?.privacyVersion,
      ),
  });
}
